import { useEffect, useState, useRef } from "react";
import { nextTick } from "common/utils";
import { Switch } from "antd";
import { noop } from "lodash-es";
import { useQuery } from "react-query";

import { Qualifiers } from "common/components/queryBuilder/qualifiers";
import { Paginator } from "common/components/paginator/paginator";
import { WebixDatatable } from "common/components/webix/react/Datatable";

export function DetailsTable({
    ui,
    queryKey,
    queryFn,
    queryDeps,
    renderDetails,
    liveUpdates = true,
    initialDetails = null,
    addFilter = noop,
    refetchInterval = 5000
}) {
    const [details, setDetails] = useState(initialDetails);
    const closeDetails = (e) => {
        e.preventDefault();
        setDetails(null);
    };

    const [shouldUpdate, setShouldUpdate] = useState(liveUpdates);

    const [page, setPage] = useState(1);

    const tableRef = useRef();

    const getHeight = () => window.innerHeight - 200; // TODO replace this with flexbox?

    useEffect(() => {
        const resizeTable = () => {
            if (tableRef.current) {
                tableRef.current.table.current.ui.define("height", getHeight());
            }
        };

        window.addEventListener("resize", resizeTable);
        return () => window.removeEventListener("resize", resizeTable);
    }, []);

    const { data, status, isPreviousData } = useQuery(
        [queryKey, { page, ...queryDeps }],
        queryFn,
        {
            keepPreviousData: true, // continue to show old data while new data loads
            ...shouldUpdate && { refetchInterval, staleTime: 0 } // poll for new results only when updating enabled
        }
    );

    const { results, count, limit, next } = data || {};

    const tableUI = {
        ...ui,
        height: getHeight(),
        leftSplit: 2,
        on: {
            onItemClick({ row, column }, e, obj) {
                nextTick(() => {
                    const item = this.getItem(row);

                    if (column === "_id") {
                        // _id column controls details
                        setDetails(item);
                    } else {
                        // All other columns just apply a filter to results
                        const columnFilter = ui.columns.find(({ id }) => id === column)?.filter || column;

                        if (item[columnFilter]) {
                            addFilter(`${columnFilter}:${Qualifiers.normalizeValue(item[columnFilter])}`);
                        }
                    }
                });
            }
        }
    };

    return (
        <div className="request-details requests-body">
            {status === "loading" && !results && (
                <>
                    <div className="paginator-wrapper top">
                        <Paginator loading />
                    </div>
                    <div className="loader text-input__loading--line medium" />
                    <div className="loader text-input__loading--line long" />
                </>
            )}

            {status === "error" && "There was an error fetching your request"}

            {results && (
                <>
                    <div className="paginator-wrapper top">
                        {liveUpdates && page === 1 && (
                            <div className="slider">
                                <Switch
                                    checkedChildren="Live"
                                    unCheckedChildren="Live"
                                    checked={shouldUpdate}
                                    onChange={setShouldUpdate}
                                />
                            </div>
                        )}
                        <Paginator
                            limit={limit}
                            count={count}
                            page={page}
                            next={next}
                            results_length={results.length}
                            onPageChange={(p) => !isPreviousData && setPage(p)}
                        />
                    </div>

                    <WebixDatatable
                        ref={tableRef}
                        className="report-grid"
                        ui={tableUI}
                        data={results}
                    />

                    <div className="paginator-wrapper bottom">
                        <Paginator
                            limit={limit}
                            count={count}
                            page={page}
                            next={next}
                            results_length={results.length}
                            onPageChange={(p) => !isPreviousData && setPage(p)}
                        />
                    </div>
                </>

            )}
            {details && renderDetails({ closeDetails, details })}
        </div>
    );
}
